import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
} from '@mui/material';

import { r, color, radius, shadow } from '../../../theme/tokens';
import { DELETE_DIALOG } from '../copy';

/**
 * The delete confirmation — §7's dialog, and the only action on this page that
 * interrupts. Everything else fires a toast with Undo; delete is irreversible,
 * so it asks first and offers no undo afterwards.
 *
 * §7's spec: scrim `rgba(12,31,23,0.45)`, 420px panel, paper, 16px radius,
 * `--shadow-modal`, 24px padding, title 20px/800, buttons right-aligned as
 * secondary Cancel then primary Delete stack, Esc and scrim click cancel, focus
 * trapped and returned to the Delete control on close.
 *
 * Focus trapping and the Esc/scrim cancels come from MUI's `Dialog`, which is
 * what the frontend rules require for overlays. Focus *return* is MUI's
 * default too — it restores to the element that had focus when the dialog
 * opened, which is the Delete control that opened it — so nothing here
 * re-implements it; the test asserts the behaviour rather than the mechanism.
 *
 * The confirm button is `--destructive`, not the accent. §7's open point asked
 * for a design-system amendment to allow a red destructive button; that
 * amendment shipped on 2026-08-08 (STACK-501), as a token separate from
 * interaction-risk red. Red = this destroys something; amber = this may harm
 * you. §7's premise is simply out of date.
 */
export default function DeleteStackDialog({
  open,
  stackName,
  supplementCount,
  onCancel,
  onConfirm,
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="delete-stack-title"
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(12, 31, 23, 0.45)' } },
      }}
      PaperProps={{
        sx: {
          width: '26.25rem', // 420px
          maxWidth: 'calc(100vw - 2rem)',
          backgroundColor: color.paper,
          borderRadius: radius.lg,
          boxShadow: shadow.modal,
          padding: '1.5rem',
        },
      }}
    >
      <DialogTitle
        id="delete-stack-title"
        sx={{
          padding: 0,
          fontSize: '1.25rem', // 20px
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: color.ink,
        }}
      >
        {DELETE_DIALOG.title(stackName)}
      </DialogTitle>

      <DialogContent sx={{ padding: 0, marginTop: '0.75rem' }}>
        <DialogContentText
          sx={{ color: color.inkSoft, fontSize: '0.875rem', lineHeight: 1.5 }}
        >
          {DELETE_DIALOG.body(supplementCount)}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ padding: 0, marginTop: '1.5rem', gap: '0.5rem' }}>
        <Button
          onClick={onCancel}
          sx={{
            minHeight: '2.75rem',
            paddingInline: '1rem',
            borderRadius: radius.md,
            border: `${r(1)} solid ${color.line}`,
            backgroundColor: color.paper,
            color: color.ink,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { backgroundColor: color.surface },
          }}
        >
          {DELETE_DIALOG.cancel}
        </Button>
        <Button
          onClick={onConfirm}
          sx={{
            minHeight: '2.75rem',
            paddingInline: '1rem',
            borderRadius: radius.md,
            backgroundColor: color.destructive,
            color: color.paper,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { backgroundColor: color.destructive, filter: 'brightness(0.92)' },
          }}
        >
          {DELETE_DIALOG.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
